import React, { useContext, useEffect, useState } from "react";
import { createSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

/*******INTERNAL IMPORTS********/
import { DisplaySpinnerContext } from "../../../context/ContextIndex";
import {
  DataGridTable,
  GridAction,
  ConfirmationPopup,
  notifyToast,
  ToastType,
  FormPage
} from "../../../components-old/ComponentsIndex";
import { PricingPortalAPI } from "../../../apis/api-index";
import { MdDelete, MdCreate } from "react-icons/md";
import AddIcon from "@mui/icons-material/Add";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import { useSelector } from "react-redux";
import { selectSessionConfig } from "../../../store/store-selector";
import { AppUtility } from "../../../utils/utility";
import { saveServiceType } from "../api-handler/api-handler";

const ServiceTypeListForm = () => {
  /**************************************************
  //Form handlers & hooks
  **************************************************/
  const { t } = useTranslation();
  const navigate = useNavigate();
  const spinnerCtx = useContext(DisplaySpinnerContext);
  const settingsConfig = useSelector(selectSessionConfig);

  /**************************************************
  //Form state variables
  **************************************************/
  const [serviceTypeRecordList, setServiceTypeRecordList] = useState([]);
  const [dataGridPageSize, setDataGridPageSize] = useState(10);
  const [totalServiceType, setTotalServiceType] = useState(0);
  const [gridPageSizeOptions /*setGridPageSizeOptions*/] = useState([10, 20, 50]);
  const [popupOpen, setPopupOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);


  /**************************************************
  //Event definitions
  **************************************************/

  //on click of add icon for creating service type
  const handleCreateServiceTypeClick = () => {
    navigate("/serviceType");
  };

  //onclick of edit service type
  const handleEditServiceTypeClick = async (param) => {
    let _navigationData = param.recordResponse;
    delete _navigationData.CreatedDate;
    delete _navigationData.ModifiedDate;
    delete _navigationData.CountryCode;
    delete _navigationData.BusinessCode;

    navigate(
      {
        pathname: "/serviceType",
        search: createSearchParams({
          typeid: param.recordResponse.ServiceTypeId,
        }).toString(),
      },
      { state: _navigationData }
    );
  };

  //onclick of confirm of delete popup
  const handleConfirm = async () => {
    let _filterClause = `&typeid=${selectedRow.id}`;
    await fetchAvailableDevice(_filterClause, selectedRow);
    setPopupOpen(false);
    setSelectedRow(null);
  };

  //on click of cancel of delete popup
  const handleCancel = () => {
    setSelectedRow(null);
    setPopupOpen(false);
  };

  //onClick of delete in list
  const showDeleteServiceTypePopup = (event) => {
    try {
      setSelectedRow(event);
      setPopupOpen(true);
    } catch (err) {
      alert(err);
    }
  };

  //on page change
  const handlePageSizeChange = (newPageSize) => {
    setDataGridPageSize(newPageSize);
  };

  //show review modal
  const handleShowSelectedServiceTypeChanges = (event) => {
    let _navigationData = { previousPath: "/servicetypelist" };
    navigate(
      {
        pathname: "/reviewdetails",
        search: createSearchParams({
          reviewFilter: `&locationkey=${event.id}`,
        }).toString(),
      },
      { state: _navigationData }
    );
  };

  /**************************************************
  //Fetch API & utility
  **************************************************/


  const setServiceTypeList = (serviceTypeList) => {
    try {
      serviceTypeList.sort((a, b) => new Date(b.ModifiedDate).getTime() - new Date(a.ModifiedDate).getTime());
      let _serviceTypeList = serviceTypeList.map((item) => {
        return {
          id: item.ServiceTypeId,
          serviceName: item.ServiceName,
          grossMarginContracts: item.GrossMarginContracts,
          grossMarginJobs: item.GrossMarginJobs,
          recordResponse: item
        };
      });

      setServiceTypeRecordList(_serviceTypeList);
      setTotalServiceType(_serviceTypeList.length);
      spinnerCtx.setLoadingIndicator(false);
    } catch (err) {
      spinnerCtx.setLoadingIndicator(false);
      alert(err);
    }
  };

  const FetchServiceTypeList = async () => {
    try {
      let _lists = await PricingPortalAPI.getList(
        settingsConfig.businessCode,
        settingsConfig.countryCode,
        "getServiceTypeList",
        "servicetype"
      );
      setServiceTypeList(_lists);
    } catch (err) {
      alert(err);
    }
  };
  //fetch available device before deleting service type
  const fetchAvailableDevice = async (filter, param) => {
    try {
      let _availableDevice = await PricingPortalAPI.getFilterList(
        settingsConfig.businessCode,
        settingsConfig.countryCode,
        "getDeviceList",
        "device",
        filter
      );

      if (_availableDevice && _availableDevice.length > 0) {
        notifyToast("Device combination exists with this service type", ToastType.ERROR);
      } else {
        await fetchAvailableGrossBranch(filter, param);
      }
    } catch (e) {
      alert(e);
    }
  };

  //fetch gross branch before deleting service type
  const fetchAvailableGrossBranch = async (filter, param) => {
    try {
      let _availableGrossBranch = await PricingPortalAPI.getFilterList(
        settingsConfig.businessCode,
        settingsConfig.countryCode,
        "getGrossBranchList",
        "grossbranch",
        filter
      );

      if (_availableGrossBranch && _availableGrossBranch.length > 0) {
        notifyToast("Gross branch combination exists with this service type", ToastType.ERROR);
      } else {
        await deleteServiceType(param);
      }
    } catch (e) {
      alert(e);
    }
  };

  const formPreviousDataForDelete = (param) => {
    try {
      let _previousState = {};
      _previousState.Deleted = true;
      _previousState.ServiceName = param.serviceName;
      _previousState.GrossMarginContracts = param.grossMarginContracts;
      _previousState.GrossMarginJobs = param.grossMarginJobs;

      return _previousState ? _previousState : null;
    } catch (e) {
      alert(e);
    }
  };

  //Delete API call
  const deleteServiceType = async (param) => {
    try {
      spinnerCtx.setLoadingIndicator(true);
      let EditServiceTypeId = param.id;
      const status = await saveServiceType(formPreviousDataForDelete(param), {
        EditServiceTypeId,
        settingsConfig
      });

      if (status?.responseStatus === 200) {
        notifyToast("Sent for Approval", ToastType.SUCCESS);
        await FetchServiceTypeList();
      } else {
        notifyToast(status.errorMessage ? status.errorMessage : "Service type deletion failed", ToastType.ERROR);
        spinnerCtx.setLoadingIndicator(false);
      }
    } catch (err) {
      alert(err);
    }
  };

  /**************************************************
  //JSX delcaration
  **************************************************/

  //data grid schema
  const dataGridSchema = [
    {
      field: "serviceName",
      headerName: t("serviceTypeList.serviceName"),
      flex: 2,
    },
    {
      field: "grossMarginContracts",
      headerName: t("serviceTypeList.grossMarginContracts"),
      flex: 1,
    },
    {
      field: "grossMarginJobs",
      headerName: t("serviceTypeList.grossMarginJobs"),
      flex: 1,
    },
    {
      field: "recordResponse",
      hide: true,
    },
    {
      field: "action",
      headerName: t,
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        return (
          <GridAction actionsConfig={actionConfigs} row={params.row} />
        );
      },
    },
  ];

  //button configs for add and download list
  const buttonConfigs = [
    {
      buttonText: <AddIcon />,
      buttonType: "filled",
      onClick: handleCreateServiceTypeClick,
      buttonColor: "#A62B23",
      textColor: "#fff",
      action: "create"
    },
    {
      buttonText: <SystemUpdateAltIcon />,
      buttonType: "outlined",
      onClick: () => {
        AppUtility.handleDownload(serviceTypeRecordList, "servicetypelist.xlsx");
      },
      buttonColor: "#A62B23",
      textColor: "#A62B23",
      action: "download"
    },
  ];

  //action configs for edit and delete record
  const actionConfigs = [
    {
      onClick: handleShowSelectedServiceTypeChanges,
      action: "review"
    },
    {
      icon: MdCreate,
      onClick: handleEditServiceTypeClick,
      buttonColor: "#EEEEE",
      action: "edit"
    },
    {
      icon: MdDelete,
      onClick: showDeleteServiceTypePopup,
      buttonColor: "#A62B23",
      action: "delete"
    },
  ];


  /**************************************************
  //Form on load useEffect
  **************************************************/
  useEffect(() => {

    spinnerCtx.setLoadingIndicator(true);
    FetchServiceTypeList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormPage isList={true}>
      <DataGridTable
        rows={serviceTypeRecordList}
        columns={dataGridSchema}
        pageSize={dataGridPageSize}
        pageSizeOptions={gridPageSizeOptions}
        onPageSizeChange={handlePageSizeChange}
        totalRowCount={totalServiceType}
        toolbarHeader={t("serviceTypeList.header")}
        toolbarFilterPlaceholder={t("quoteApproval.toolbarFilterPlaceholder")}
        buttonConfigs={buttonConfigs}
      />
      <ConfirmationPopup
        isOpen={popupOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        confirmationText={t("popup.deleteServiceTypeConfirmationMessage")}
        eventObj={selectedRow}
      />
    </FormPage>
  );
};

export default ServiceTypeListForm;
